import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { getUpcomingEventsForReminders, getEventAttendees } from '../lib/eventReminders';

const NotificationPanel = ({ userId, userType }) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    fetchNotifications();
  }, [userId, userType]);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const notifs = [];

      // Get upcoming events the user is registered for (attendee)
      if (userType === 'attendee') {
        try {
          const { data: registrations, error } = await supabase
            .from('event_registrations')
            .select(`
              event_id,
              events (
                id,
                title,
                start_date,
                end_date,
                location,
                venue_name
              )
            `)
            .eq('user_id', userId)
            .eq('status', 'registered');

          if (!error && registrations) {
            const today = new Date();
            registrations.forEach(reg => {
              const event = reg.events;
              if (event) {
                const eventDate = new Date(event.start_date);
                const daysUntil = Math.ceil((eventDate - today) / (1000 * 60 * 60 * 24));

                if (daysUntil >= 0 && daysUntil <= 14) {
                  notifs.push({
                    id: `event-${event.id}`,
                    type: 'event_reminder',
                    title: 'Upcoming Event',
                    message: `${event.title} is in ${daysUntil} days`,
                    date: event.start_date,
                    event: event,
                    actionText: 'View Details',
                    actionUrl: `/events/${event.id}`
                  });
                }
              }
            });
          }
        } catch (err) {
          console.error('Error fetching attendee registrations:', err);
        }
      }

      // Get upcoming events for church admin (their events)
      if (userType === 'church') {
        try {
          const { data: churchEvents, error } = await supabase
            .from('events')
            .select('*')
            .eq('created_by', userId)
            .gte('start_date', new Date().toISOString().split('T')[0])
            .order('start_date', { ascending: true })
            .limit(5);

          if (!error && churchEvents) {
            for (const event of churchEvents) {
              try {
                const eventDate = new Date(event.start_date);
                const daysUntil = Math.ceil((eventDate - new Date()) / (1000 * 60 * 60 * 24));

                // Get registration count
                try {
                  const attendees = await getEventAttendees(event.id);
                  notifs.push({
                    id: `church-event-${event.id}`,
                    type: 'church_event',
                    title: 'Your Upcoming Event',
                    message: `${event.title} has ${attendees?.length || 0} registered attendees`,
                    date: event.start_date,
                    event: event,
                    actionText: 'Manage Event',
                    actionUrl: `/church/events/${event.id}/edit`
                  });
                } catch (permError) {
                  // If we can't get attendee count, still show the event
                  notifs.push({
                    id: `church-event-${event.id}`,
                    type: 'church_event',
                    title: 'Your Upcoming Event',
                    message: `${event.title} - ${daysUntil} days away`,
                    date: event.start_date,
                    event: event,
                    actionText: 'Manage Event',
                    actionUrl: `/church/events/${event.id}/edit`
                  });
                }
              } catch (err) {
                console.error(`Error processing event ${event.id}:`, err);
              }
            }
          }
        } catch (err) {
          console.error('Error fetching church events:', err);
        }
      }

      // Sort by date
      notifs.sort((a, b) => new Date(a.date) - new Date(b.date));
      setNotifications(notifs);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      // Set empty notifications to prevent infinite loading
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'event_reminder':
        return (
          <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
            <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        );
      case 'church_event':
        return (
          <div className="flex-shrink-0 w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
            <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          </div>
        );
      default:
        return (
          <div className="flex-shrink-0 w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
            <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        );
    }
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 mb-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded"></div>
            <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-5/6"></div>
          </div>
        </div>
      </div>
    );
  }

  // Empty state - no notifications
  if (!loading && notifications.length === 0 && userId) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 mb-6">
        <div className="p-4 border-b border-gray-100 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Notifications</h3>
        </div>
        <div className="p-6 text-center text-gray-500 dark:text-gray-400">
          <svg className="mx-auto h-10 w-10 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
          </svg>
          <p className="mt-2 text-sm">No new notifications</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 mb-6">
      <div className="p-4 border-b border-gray-100 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Notifications</h3>
          {notifications.length > 0 && (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300">
              {notifications.length}
            </span>
          )}
        </div>
      </div>
      
      <div className="divide-y divide-gray-100 dark:divide-gray-700">
        {notifications.slice(0, expanded ? notifications.length : 3).map((notification) => (
          <div key={notification.id} className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
            <div className="flex items-start">
              {getNotificationIcon(notification.type)}
              <div className="ml-3 flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-white">{notification.title}</p>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 truncate">{notification.message}</p>
                <div className="mt-2 flex items-center justify-between">
                  <p className="text-xs text-gray-400 dark:text-gray-500">
                    {new Date(notification.date).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </p>
                  {notification.actionUrl && (
                    <a
                      href={notification.actionUrl}
                      className="text-xs text-primary hover:text-primary-dark font-medium"
                    >
                      {notification.actionText} →
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
        
        {notifications.length > 3 && (
          <div className="p-3 text-center bg-gray-50 dark:bg-gray-700/30">
            <button
              onClick={() => setExpanded(!expanded)}
              className="text-sm text-primary hover:text-primary-dark font-medium"
            >
              {expanded ? 'Show Less' : `View All (${notifications.length})`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationPanel;
